import type { MemoryOrigin } from "@/features/material-compliance/api/types"
import type { TranslationKeys } from "@/hooks/useTranslation/catalogs"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ChevronDown, ChevronRight, History } from "lucide-react"
import { useState } from "react"

import { MemoryLineageSheet } from "./MemoryLineageSheet"
import { renderMemorySource } from "./memorySourceLink"

const TRUST_LABEL: Record<string, TranslationKeys> = {
  explicit_correction: "MC_MEMORY_TRUST_CORRECTION",
  confirmed_document: "MC_MEMORY_TRUST_DOCUMENT",
  chat_mined: "MC_MEMORY_TRUST_CHAT",
  derived: "MC_MEMORY_TRUST_DERIVED",
}

export function formatMemoryDate(isoDate: string, locale: string): string {
  const [year, month, day] = isoDate.split("-").map(Number)
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export interface MemoryTrailProps {
  origin: MemoryOrigin | null | undefined
  /** Suffix for the test id, so two trails on one conflict are addressable apart. */
  testId: string
  /** The entry this trail is about — required to open "Full history", the one other way
   *  into memory's tables (`GET .../memory/{entryId}/lineage`). Optional and omittable:
   *  a caller with no entry id yet (a withheld hint, a conflict side with none resolved)
   *  simply gets no "Full history" affordance, same as today. */
  entryId?: string | null
  /** The surface this trail renders on — required by the lineage endpoint for the same
   *  reason accept/revert require it: proving which screen's entry this is. */
  surface?: string
}

/**
 * Where a remembered value came from, collapsed until somebody asks.
 *
 * WHY COLLAPSED. `because` is the glance — one sentence, always visible. This is the second
 * question, and it is only asked at the moment somebody doubts the suggestion or has to
 * choose between two that disagree. Showing it open by default would put four lines of
 * provenance under every suggestion on a sixty-question form, which is how people learn to
 * stop reading the box entirely.
 *
 * WHY IT EXISTS AT ALL. On a compliance field, "0.05 %" with no traceable origin is either
 * trusted blindly — which is how an indefensible number reaches a customer — or it discredits
 * the feature. `because` names the source; this says what KIND of claim it was. "A person
 * corrected this" and "a document asserted this" carry completely different weight to a
 * reviewer, and that distinction is not recoverable from the sentence.
 *
 * DATES ARE FORMATTED HERE, not on the server. The server's `because` line is built in
 * English with an English date format, which is a known defect — a German reviewer reads a
 * German label wrapped around an English sentence. Everything this component renders is
 * assembled from parts in the reader's own locale, so the trail is already right while
 * `because` is still being fixed.
 *
 * THE ACTOR, WHEN THERE IS ONE TO NAME. `actorName` is null on some surfaces even when a
 * real person made the decision — the questionnaire surfaces record a uuid today, which
 * names nobody, and printing it would be worse than the blank it replaces. "you" is said
 * instead of the reader's own name when `isYou` is true, which needs no lookup at all and
 * is the one case guaranteed to be right; otherwise this says "a colleague" rather than
 * dropping attribution silently — this collapsible IS the place that names it (decided
 * after testing: the always-visible glance line (`MemoryBecause`) stays neutral so it never
 * reads as pointing a finger, but this detail, opened on purpose, should say who it was).
 * Gated on `trustClass === "explicit_correction"`, the same guard `MemoryBecause` uses —
 * neither "you" nor "a colleague" means anything for a value nobody deliberately decided.
 *
 * THE SOURCE LINKS WHEN IT SAFELY CAN. A declaration has one canonical path
 * (`declarationReviewPath`); a questionnaire's path depends on its upload type (PDF vs.
 * Excel), which this trail has no way to know, so linking it would risk a dead link more
 * often than it would help. Named-not-linked there is a known, deliberate gap — not an
 * oversight — until the read side carries enough to resolve it safely.
 */
export function MemoryTrail({
  origin,
  testId,
  entryId,
  surface,
}: MemoryTrailProps) {
  const { translate, currentLanguage } = useTranslation()
  const [open, setOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)

  if (!origin) return null

  const trustKey = TRUST_LABEL[origin.trustClass]
  // An unrecognised trust class renders as nothing rather than as a raw enum value. The
  // vocabulary can grow on the server before this catalogue knows the word, and a reviewer
  // reading "chat_mined" learns less than one reading only the source and the date.
  const trust = trustKey ? translate(trustKey) : null

  const when = origin.occurredOn
    ? formatMemoryDate(origin.occurredOn, currentLanguage.toLowerCase())
    : null

  const explicitDecision = origin.trustClass === "explicit_correction"
  const who = !explicitDecision
    ? null
    : origin.isYou
      ? translate("MC_MEMORY_BY_YOU")
      : (origin.actorName ?? translate("MC_MEMORY_BY_COLLEAGUE"))

  const source = renderMemorySource(
    origin,
    translate("MC_MEMORY_OPEN_SOURCE"),
    testId,
  )

  const parts: React.ReactNode[] = [trust, source, when, who].filter(Boolean)
  if (parts.length === 0) return null

  return (
    <div className="flex flex-col">
      <button
        type="button"
        className="flex items-center gap-1 self-start text-[10px] text-grey5 transition-colors hover:text-white"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid={`memory-trail-toggle-${testId}`}
      >
        {open ? (
          <ChevronDown size={10} aria-hidden="true" />
        ) : (
          <ChevronRight size={10} aria-hidden="true" />
        )}
        {translate("MC_MEMORY_WHERE_FROM")}
      </button>
      {open ? (
        <>
          <p
            className="mt-[2px] flex flex-wrap items-center gap-x-1 pl-[14px] text-[10px] text-grey5"
            data-testid={`memory-trail-${testId}`}
          >
            {parts.map((part, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 ? <span aria-hidden="true">·</span> : null}
                {part}
              </span>
            ))}
          </p>
          {entryId && surface ? (
            <button
              type="button"
              className="mt-[2px] flex items-center gap-1 self-start pl-[14px] text-[10px] text-grey5 underline transition-colors hover:text-white"
              onClick={() => setHistoryOpen(true)}
              data-testid={`memory-trail-history-${testId}`}
            >
              <History size={10} aria-hidden="true" />
              {translate("MC_MEMORY_FULL_HISTORY")}
            </button>
          ) : null}
        </>
      ) : null}
      {entryId && surface ? (
        <MemoryLineageSheet
          entryId={entryId}
          surface={surface}
          open={historyOpen}
          onOpenChange={setHistoryOpen}
        />
      ) : null}
    </div>
  )
}
